import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Upload,
  User,
  Image,
  Phone,
  Mail,
  Quote,
  Award,
  List,
  Eye,
  Target,
  Sparkles,
  X,
  Plus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FounderEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);
  const [uploadingAction, setUploadingAction] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false); // Ref for event handlers
  const portraitInputRef = useRef(null);
  const actionInputRef = useRef(null);

  const [config, setConfig] = useState({
    name: '',
    role: '',
    org: '',
    headline: '',
    subheadline: '',
    bio: '',
    quote: '',
    phone: '',
    email: '',
    portrait_url: null,
    action_image_url: null,
    cta_primary_text: '',
    cta_primary_link: '',
    cta_secondary_text: '',
    cta_secondary_link: '',
    authority_badges: [],
    vision_title: '',
    vision_text: '',
    system_title: '',
    system_checklist: [],
    action_headline: '',
    action_text: '',
    cta_section_headline: '',
    cta_section_text: '',
    cta_enroll_text: '',
    cta_partner_text: '',
  });

  // Track form changes
  const updateConfig = useCallback((updater) => {
    setConfig(prev => {
      const newConfig = typeof updater === 'function' ? updater(prev) : updater;
      return newConfig;
    });
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Safe navigation helper with unsaved changes check
  const handleNavigateBack = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (confirmed) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/founder-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load founder config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/founder-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });

      if (res.ok) {
        toast.success('Founder content saved successfully');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig(); // Refetch to confirm
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      toast.error('Failed to save founder config');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file, imageType) => {
    const setUploading = imageType === 'founder_portrait' ? setUploadingPortrait : setUploadingAction;
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);

      const res = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        const fieldName = imageType === 'founder_portrait' ? 'portrait_url' : 'action_image_url';
        updateConfig(prev => ({ ...prev, [fieldName]: data.image_url }));
        toast.success(`${imageType === 'founder_portrait' ? 'Portrait' : 'Action image'} uploaded`);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleBadgeChange = (index, value) => {
    const newBadges = [...config.authority_badges];
    newBadges[index] = { label: value };
    updateConfig(prev => ({ ...prev, authority_badges: newBadges }));
  };

  const handleChecklistChange = (index, value) => {
    const newChecklist = [...config.system_checklist];
    newChecklist[index] = value;
    updateConfig(prev => ({ ...prev, system_checklist: newChecklist }));
  };

  const addChecklistItem = () => {
    updateConfig(prev => ({ ...prev, system_checklist: [...prev.system_checklist, ''] }));
  };

  const removeChecklistItem = (index) => {
    const newChecklist = config.system_checklist.filter((_, i) => i !== index);
    updateConfig(prev => ({ ...prev, system_checklist: newChecklist }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto p-6" data-testid="founder-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNavigateBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Founder Page Editor</h1>
            <p className="text-slate-500">Edit content displayed on Homepage and /founder page</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => window.open('/founder', '_blank')}
            className="flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
            data-testid="save-founder-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Images Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-primary" />
            Images
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Portrait */}
            <div>
              <Label className="mb-2 block">Founder Portrait (4:5 ratio recommended)</Label>
              <input
                ref={portraitInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'founder_portrait')}
              />
              <div
                onClick={() => portraitInputRef.current?.click()}
                className="relative aspect-[4/5] max-w-[200px] rounded-xl overflow-hidden border-2 border-dashed border-slate-300 hover:border-primary cursor-pointer transition-colors bg-slate-50"
              >
                {config.portrait_url ? (
                  <img
                    src={getImageUrl(config.portrait_url)}
                    alt="Portrait"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                    <User className="w-12 h-12 mb-2" />
                    <span className="text-sm">Click to upload</span>
                  </div>
                )}
                {uploadingPortrait && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                )}
              </div>
            </div>

            {/* Action Image */}
            <div>
              <Label className="mb-2 block">In-Action Image (16:9 ratio recommended)</Label>
              <input
                ref={actionInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'founder_action')}
              />
              <div
                onClick={() => actionInputRef.current?.click()}
                className="relative aspect-video max-w-[320px] rounded-xl overflow-hidden border-2 border-dashed border-slate-300 hover:border-primary cursor-pointer transition-colors bg-slate-50"
              >
                {config.action_image_url ? (
                  <img
                    src={getImageUrl(config.action_image_url)}
                    alt="In Action"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                    <Upload className="w-12 h-12 mb-2" />
                    <span className="text-sm">Click to upload</span>
                  </div>
                )}
                {uploadingAction && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Identity Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Founder Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={config.name}
                onChange={(e) => updateConfig(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Sam Oyelakun"
                data-testid="founder-name-input"
              />
            </div>
            <div>
              <Label htmlFor="headline">Page Headline</Label>
              <Input
                id="headline"
                value={config.headline}
                onChange={(e) => updateConfig(prev => ({ ...prev, headline: e.target.value }))}
                placeholder="Meet the Founder"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="role">Role / Title</Label>
              <Input
                id="role"
                value={config.role}
                onChange={(e) => updateConfig(prev => ({ ...prev, role: e.target.value }))}
                placeholder="Founder & Head Coach"
              />
            </div>
            <div>
              <Label htmlFor="org">Organization / Affiliation</Label>
              <Input
                id="org"
                value={config.org}
                onChange={(e) => updateConfig(prev => ({ ...prev, org: e.target.value }))}
                placeholder="Regional Coordinator – Eswatini Drowning Prevention Alliance"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="subheadline">Subheadline (Homepage)</Label>
            <Input
              id="subheadline"
              value={config.subheadline}
              onChange={(e) => updateConfig(prev => ({ ...prev, subheadline: e.target.value }))}
              placeholder="Water Safety Advocate. Swim Coach. Drowning Prevention Leader."
            />
          </div>
          <div>
            <Label htmlFor="bio">Bio (Homepage)</Label>
            <Textarea
              id="bio"
              rows={4}
              value={config.bio}
              onChange={(e) => updateConfig(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Brief biography for homepage..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Quote Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Quote className="w-5 h-5 text-primary" />
            Signature Quote
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="quote">Quote</Label>
            <Input
              id="quote"
              value={config.quote}
              onChange={(e) => updateConfig(prev => ({ ...prev, quote: e.target.value }))}
              placeholder="Water safety is not a luxury — it is a life skill."
              data-testid="founder-quote-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={config.phone}
                onChange={(e) => updateConfig(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+268 7600 1234"
                data-testid="founder-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={config.email}
                onChange={(e) => updateConfig(prev => ({ ...prev, email: e.target.value }))}
                placeholder="info@eswatiniseals.com"
                data-testid="founder-email-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Authority Badges */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            Authority Badges (4 badges)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.authority_badges.map((badge, idx) => (
              <div key={idx}>
                <Label>Badge {idx + 1}</Label>
                <Input
                  value={badge.label || ''}
                  onChange={(e) => handleBadgeChange(idx, e.target.value)}
                  placeholder={`Badge ${idx + 1} label`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Vision Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Vision Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="vision_title">Vision Title</Label>
            <Input
              id="vision_title"
              value={config.vision_title}
              onChange={(e) => updateConfig(prev => ({ ...prev, vision_title: e.target.value }))}
              placeholder="Building Water-Confident Generations"
            />
          </div>
          <div>
            <Label htmlFor="vision_text">Vision Text</Label>
            <Textarea
              id="vision_text"
              rows={3}
              value={config.vision_text}
              onChange={(e) => updateConfig(prev => ({ ...prev, vision_text: e.target.value }))}
              placeholder="Vision description..."
            />
          </div>
        </CardContent>
      </Card>

      {/* System Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <List className="w-5 h-5 text-primary" />
            System Checklist
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="system_title">Section Title</Label>
            <Input
              id="system_title"
              value={config.system_title}
              onChange={(e) => updateConfig(prev => ({ ...prev, system_title: e.target.value }))}
              placeholder="A Structured Approach to Safety"
            />
          </div>
          <div className="space-y-3">
            <Label>Checklist Items</Label>
            {config.system_checklist.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => handleChecklistChange(idx, e.target.value)}
                  placeholder={`Item ${idx + 1}`}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeChecklistItem(idx)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addChecklistItem} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Item
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Action Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            In-Action Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="action_headline">Headline</Label>
            <Input
              id="action_headline"
              value={config.action_headline}
              onChange={(e) => updateConfig(prev => ({ ...prev, action_headline: e.target.value }))}
              placeholder="Where Leadership Meets Action"
            />
          </div>
          <div>
            <Label htmlFor="action_text">Description</Label>
            <Textarea
              id="action_text"
              rows={2}
              value={config.action_text}
              onChange={(e) => updateConfig(prev => ({ ...prev, action_text: e.target.value }))}
              placeholder="Sam actively trains learners..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Final CTA Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Final CTA Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="cta_section_headline">Headline</Label>
            <Input
              id="cta_section_headline"
              value={config.cta_section_headline}
              onChange={(e) => updateConfig(prev => ({ ...prev, cta_section_headline: e.target.value }))}
              placeholder="Building Water-Safe Communities..."
            />
          </div>
          <div>
            <Label htmlFor="cta_section_text">Description</Label>
            <Textarea
              id="cta_section_text"
              rows={2}
              value={config.cta_section_text}
              onChange={(e) => updateConfig(prev => ({ ...prev, cta_section_text: e.target.value }))}
              placeholder="Whether you're enrolling a child..."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Primary CTA Text</Label>
              <Input
                value={config.cta_primary_text}
                onChange={(e) => updateConfig(prev => ({ ...prev, cta_primary_text: e.target.value }))}
                placeholder="View Programs"
              />
            </div>
            <div>
              <Label>Primary CTA Link</Label>
              <Input
                value={config.cta_primary_link}
                onChange={(e) => updateConfig(prev => ({ ...prev, cta_primary_link: e.target.value }))}
                placeholder="/#programs"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Secondary CTA Text</Label>
              <Input
                value={config.cta_secondary_text}
                onChange={(e) => updateConfig(prev => ({ ...prev, cta_secondary_text: e.target.value }))}
                placeholder="Partner With Us"
              />
            </div>
            <div>
              <Label>Secondary CTA Link</Label>
              <Input
                value={config.cta_secondary_link}
                onChange={(e) => updateConfig(prev => ({ ...prev, cta_secondary_link: e.target.value }))}
                placeholder="/#contact"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Enroll Button Text</Label>
              <Input
                value={config.cta_enroll_text}
                onChange={(e) => updateConfig(prev => ({ ...prev, cta_enroll_text: e.target.value }))}
                placeholder="Enroll a Learner"
              />
            </div>
            <div>
              <Label>Partner Button Text</Label>
              <Input
                value={config.cta_partner_text}
                onChange={(e) => updateConfig(prev => ({ ...prev, cta_partner_text: e.target.value }))}
                placeholder="Partner With Us"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save Button */}
      <div className="flex justify-end pb-8">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="bg-primary hover:bg-primary/90"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default FounderEditor;
